import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '../buttons';
import OtpInputBox from '../otp-input-box';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../backdrop';
import { confirmSmsEnrollment } from './cognito.helper';

const useStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

type Props = {
  phoneNumber: string;
  onVerify: () => void;
};

const Enrollment = ({ phoneNumber, onVerify }: Props) => {
  const classes = useStyles();
  const [otp, setOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const notificationContext = useContext(NotificationsContext);

  const { t, inlineRichT } = useTranslation();

  const verifyCode = async () => {
    if (otp) {
      setIsLoading(true);
      try {
        await confirmSmsEnrollment(otp);
        showNotification(t('TWO_STEP_AUTH_ENROLLMENT_SUCCESS'));
        onVerify();
      } catch (error) {
        if (isInvalidCodeError(error)) {
          showNotification(t('TWO_STEP_AUTH_INVALID_ONE_TIME_CODE'), 'error');
        } else {
          showNotification(
            t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'),
            'error',
          );
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      showNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
    }
  };

  const showNotification = (
    message: string,
    type: 'error' | 'success' = 'success',
  ) => {
    notificationContext.setState({
      isOpen: true,
      message: message,
      severity: type,
    });
    window.scrollTo(0, 0);
  };

  return (
    <Grid container>
      {isLoading ? <Backdrop forceOpen message={t('LOADING')} /> : null}
      <Grid item xs={12} md={12}>
        <Typography variant={'body1'}>
          {inlineRichT('TWO_STEP_AUTH_ENROLL_INFO', {
            PHONE_NUMBER: phoneNumber,
          })}
        </Typography>
      </Grid>
      <Grid item xs={12} md={12}>
        <OtpInputBox
          value={otp}
          valueLength={6}
          onChange={e => setOtp(e)}
          error=""
          isCompactMode={false}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <Button
          className={classes.button}
          variant={'outlined'}
          color={'secondary'}
          onClick={verifyCode}
          disabled={otp.length !== 6 || isLoading}
          aria-label="enroll"
        >
          {t('ENROLL')}
        </Button>
      </Grid>
    </Grid>
  );
};

export default Enrollment;

function isInvalidCodeError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const code =
    (error as { code?: string }).code ??
    (error as { name?: string }).name ??
    '';
  return (
    typeof code === 'string' &&
    (code === 'CodeMismatchException' || code === 'ExpiredCodeException')
  );
}
